/**
 * Daily Open Wizard - Multi-step bolt megnyitasi varazslo
 * Epic 61: Admin Kozpont Dashboard (Story 61-3)
 */

import { postDailyOpen, type AdminSummary } from '@/api/admin-center';
import { Button } from '@/components/ui';
import { useOpenSession } from '@/hooks/pos';
import { useWarehouseGate } from '@/hooks/use-warehouse-gate';
import { cn, formatHuf } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface DailyOpenWizardProps {
  summary: AdminSummary | null;
  onClose: () => void;
  onComplete: () => void;
}

// Step keys used for step indicator - translated in the component
const STEP_KEYS = [
  'admin.stepCashBalance',
  'admin.stepExpiredRentals',
  'admin.stepTodayBookings',
  'admin.stepServiceWaiting',
  'admin.stepReceivables',
  'admin.stepTasks',
  'admin.stepSummary',
] as const;

export function DailyOpenWizard({ summary, onClose, onComplete }: DailyOpenWizardProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [openingCashBalance, setOpeningCashBalance] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // POS session hook — also open the cash register when daily open is submitted
  const openSessionMutation = useOpenSession();
  // Q-T167: lásd a DailyCloseWizard azonos blokkját — a raktár a JWT tenantId-jából jön
  // (GET /warehouses/default), nem egy beégetett demo-tenant UUID-ból.
  const { warehouseId, isBlocked, blockMessage } = useWarehouseGate();
  const locationId = warehouseId ?? '';

  const handleNext = () => {
    if (currentStep < STEP_KEYS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Q-T167 FAIL-LOUD: raktár nélkül a kassza-nyitás némán kimaradna, és a nap úgy indulna,
    // hogy nincs nyitott munkamenet — a zárás pedig ugyanígy no-op lenne. Inkább látható hiba.
    if (isBlocked) {
      setSubmitError(blockMessage);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // 1. Record daily opening in admin-center
      await postDailyOpen({ openingCashBalance });

      // 2. Open POS cash register session with the same opening balance
      try {
        await openSessionMutation.mutateAsync({
          locationId,
          openingBalance: openingCashBalance,
        });
      } catch (posErr) {
        // POS session open failure is non-fatal (session may already be open)
        console.warn('POS session open skipped:', posErr);
      }

      onComplete();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.enterOpeningBalance')}</p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('admin.openingBalanceHuf')}
              </label>
              <input
                type="number"
                value={openingCashBalance || ''}
                onChange={e => setOpeningCashBalance(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-lg font-semibold text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                placeholder="0"
                min={0}
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('admin.enterOpeningBalance')}
              </p>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.expiredRentals')}</p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.expiredRentals')}
                </span>
                <span
                  className={cn(
                    'text-2xl font-bold',
                    (summary?.overdueRentals.count ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary?.overdueRentals.count ?? 0}
                </span>
              </div>
              {(summary?.overdueRentals.count ?? 0) > 0 && (
                <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.oldestDays', { days: '' })}
                  </span>
                  <span className="text-sm font-medium text-red-600">
                    {t('task.daysOverdue', { count: summary?.overdueRentals.oldestDays ?? 0 })}
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.todayPlanned')}</p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.todayBookings')}</span>
                <span className="text-2xl font-bold text-green-600">
                  {summary?.todayBookings.count ?? 0}
                </span>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.serviceWorksheets')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('status.waiting')}</span>
                <span className="text-xl font-bold text-amber-600">
                  {summary?.serviceWorksheets.pending ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('status.inProgress')}</span>
                <span className="text-xl font-bold text-blue-600">
                  {summary?.serviceWorksheets.inProgress ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('status.completed')}</span>
                <span className="text-xl font-bold text-green-600">
                  {summary?.serviceWorksheets.completed ?? 0}
                </span>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.receivables')}</p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.receivables')}</span>
                <span
                  className={cn(
                    'text-2xl font-bold',
                    (summary?.receivables.totalAmount ?? 0) > 0
                      ? 'text-amber-600'
                      : 'text-green-600'
                  )}
                >
                  {formatHuf(summary?.receivables.totalAmount ?? 0)}
                </span>
              </div>
              {(summary?.receivables.over30DaysPartnerCount ?? 0) > 0 && (
                <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.overdue30Partners', { count: '' })}
                  </span>
                  <span className="text-sm font-medium text-red-600">
                    {t('admin.overdue30Partners', {
                      count: summary?.receivables.over30DaysPartnerCount ?? 0,
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.tasks')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.tasks')}</span>
                <span className="text-xl font-bold text-blue-600">
                  {summary?.tasks.assigned ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.expiredTasks', { count: '' })}
                </span>
                <span
                  className={cn(
                    'text-xl font-bold',
                    (summary?.tasks.overdue ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary?.tasks.overdue ?? 0}
                </span>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.stepSummary')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.stepCashBalance')}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {formatHuf(openingCashBalance)}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.expiredRentals')}
                </span>
                <span
                  className={cn(
                    'font-bold',
                    (summary?.overdueRentals.count ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary?.overdueRentals.count ?? 0} db
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.todayBookings')}</span>
                <span className="font-bold text-green-600">
                  {summary?.todayBookings.count ?? 0} db
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.stepServiceWaiting')}
                </span>
                <span className="font-bold text-amber-600">
                  {summary?.serviceWorksheets.pending ?? 0} db
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.receivables')}</span>
                <span
                  className={cn(
                    'font-bold',
                    (summary?.receivables.totalAmount ?? 0) > 0
                      ? 'text-amber-600'
                      : 'text-green-600'
                  )}
                >
                  {formatHuf(summary?.receivables.totalAmount ?? 0)}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('task.title')}</span>
                <span className="font-bold text-blue-600">
                  {summary?.tasks.assigned ?? 0} / {summary?.tasks.overdue ?? 0}
                </span>
              </div>
            </div>
            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {submitError}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daily-open-wizard-title"
      onKeyDown={e => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="kgc-card-bg max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <div>
            <h2
              id="daily-open-wizard-title"
              className="text-xl font-semibold text-gray-900 dark:text-white"
            >
              {t('admin.dailyOpenWizard')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t(STEP_KEYS[currentStep] ?? '')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted dark:hover:text-muted-foreground"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {STEP_KEYS.map((stepKey, index) => (
              <div key={stepKey} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                    index < currentStep
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  )}
                >
                  {index < currentStep ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                {index < STEP_KEYS.length - 1 && (
                  <div
                    className={cn(
                      'h-0.5 w-4 transition-colors',
                      index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* [CR] Botka: false-affordance-őr — ha a raktár NEM oldható fel, ezt a wizard
            ELEJÉN mondjuk meg, ne a submitnál. Enélkül a user végigtölti az űrlapot,
            és a legdrágább ponton tudja meg, hogy eleve nem tud zárni/nyitni. */}
        {isBlocked && (
          <div className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {blockMessage}
          </div>
        )}

        {/* Content */}
        <div className="p-6">{renderStepContent()}</div>

        {/* Footer */}
        <div className="flex justify-between border-t border-gray-200 p-6 dark:border-gray-700">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
            {t('common.back')}
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            {currentStep < STEP_KEYS.length - 1 ? (
              <Button onClick={handleNext}>{t('common.next')}</Button>
            ) : (
              <Button
                onClick={() => void handleSubmit()}
                loading={isSubmitting}
                // [CR] Botka: false-affordance — raktár nélkül a zárás/nyitás BIZTOSAN nem
                // sikerülhet, ezért ne is legyen kattintható. A fail-loud üzenet marad
                // (handleSubmit), de a user ne a legvégén tudja meg.
                disabled={isBlocked}
                className="bg-green-600 hover:bg-green-700"
              >
                {t('admin.openStore')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
